import { DataTable, PageSpinner, useNotifications } from '@armhr/ui';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import GroupIcon from '@mui/icons-material/Group';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TableChartIcon from '@mui/icons-material/TableChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useApi } from '../../hooks/useApi';

interface CustomFieldsReportTabProps {
  fields: CustomFieldDefinition[];
}

const CustomFieldsReportTab: React.FC<CustomFieldsReportTabProps> = ({
  fields,
}) => {
  const api = useApi();
  const { showNotification } = useNotifications();

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<CustomFieldsReportData | null>(
    null
  );
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [completionFilter, setCompletionFilter] = useState<
    'all' | 'completed' | 'incomplete'
  >('all');

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.admin.getCustomFieldsReport();
      setReportData(response.results);
      setLastFetched(new Date());
    } catch (err) {
      console.error('Failed to fetch custom fields report:', err);
      setError('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleDownloadCsv = async () => {
    try {
      setDownloading(true);
      const blob = await api.admin.downloadCustomFieldsReportCsv();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'custom_fields_report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({
        message: 'Report downloaded successfully',
        severity: 'success',
        autoHideDuration: 3000,
      });
    } catch (err) {
      console.error('Failed to download CSV:', err);
      showNotification({
        message: 'Failed to download report. Please try again.',
        severity: 'error',
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleRefresh = () => {
    fetchReportData();
  };

  const handleFilterChange = (fieldKey: string, values: string[]) => {
    setFilters((prev) => ({
      ...prev,
      [fieldKey]: values,
    }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setCompletionFilter('all');
  };

  const handleRemoveFilter = (fieldKey: string, valueToRemove: string) => {
    setFilters((prev) => ({
      ...prev,
      [fieldKey]: (prev[fieldKey] || []).filter((v) => v !== valueToRemove),
    }));
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Get custom field keys from the fields prop (actual field definitions)
  const customFieldKeys = useMemo(() => {
    return fields.map((f) => f.field_key);
  }, [fields]);

  // Build filter options for ALL custom fields (extract unique values from data)
  const filterOptions = useMemo(() => {
    if (!reportData?.rows || customFieldKeys.length === 0) return {};

    const options: Record<string, string[]> = {};

    for (const key of customFieldKeys) {
      const uniqueValues = new Set<string>();
      for (const row of reportData.rows) {
        const value = row[key];
        if (value != null && value !== '') {
          uniqueValues.add(String(value));
        }
      }
      // Include all fields that have at least one value
      if (uniqueValues.size > 0) {
        options[key] = Array.from(uniqueValues).sort();
      }
    }
    return options;
  }, [reportData?.rows, customFieldKeys]);

  // Get required fields that exist in the data (for completion checking)
  const requiredFieldsInData = useMemo(() => {
    if (!reportData?.rows || reportData.rows.length === 0) return [];
    const requiredFields = fields.filter((f) => f.is_required);
    if (requiredFields.length === 0) return [];
    const firstRow = reportData.rows[0];
    return requiredFields.filter((f) => f.field_key in firstRow);
  }, [reportData?.rows, fields]);

  // Helper to check if a row has all required fields completed
  const isRowComplete = useCallback(
    (row: Record<string, unknown>) => {
      if (requiredFieldsInData.length === 0) return true; // No required fields = consider complete
      return requiredFieldsInData.every((field) => {
        const value = row[field.field_key];
        return value != null && value !== '';
      });
    },
    [requiredFieldsInData]
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      searchQuery !== '' ||
      completionFilter !== 'all' ||
      Object.values(filters).some((values) => values && values.length > 0)
    );
  }, [searchQuery, completionFilter, filters]);

  // Filter rows based on search, completion status, and custom field filters
  const filteredRows = useMemo(() => {
    if (!reportData?.rows) return [];

    return reportData.rows.filter((row) => {
      // Apply completion filter
      if (completionFilter !== 'all') {
        const complete = isRowComplete(row);
        if (completionFilter === 'completed' && !complete) return false;
        if (completionFilter === 'incomplete' && complete) return false;
      }

      // Apply search filter across all fields
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = Object.values(row).some(
          (value) =>
            value != null && String(value).toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      // Apply custom field filters (multi-select: row must match ANY of the selected values)
      for (const [key, selectedValues] of Object.entries(filters)) {
        if (selectedValues && selectedValues.length > 0) {
          const rowValue = row[key];
          if (rowValue == null || !selectedValues.includes(String(rowValue))) {
            return false;
          }
        }
      }
      return true;
    });
  }, [reportData?.rows, filters, searchQuery, completionFilter, isRowComplete]);

  // Calculate completion rate (% of employees with ALL required fields filled)
  const completionRate = useMemo(() => {
    if (!reportData?.rows || reportData.rows.length === 0) {
      return null;
    }

    const requiredFields = fields.filter((f) => f.is_required);
    if (requiredFields.length === 0) {
      return null; // No required fields = N/A
    }

    // Check if any custom field keys exist in the data
    const firstRow = reportData.rows[0];
    const requiredFieldsInData = requiredFields.filter(
      (f) => f.field_key in firstRow
    );

    // If no required custom fields are in the report data, can't calculate
    if (requiredFieldsInData.length === 0) {
      return null;
    }

    let completeEmployees = 0;

    for (const row of reportData.rows) {
      const hasAllRequired = requiredFieldsInData.every((field) => {
        const value = row[field.field_key];
        return value != null && value !== '';
      });
      if (hasAllRequired) {
        completeEmployees++;
      }
    }

    return Math.round((completeEmployees / reportData.rows.length) * 100);
  }, [reportData?.rows, fields]);

  // Format last fetched time
  const lastFetchedDisplay = useMemo(() => {
    if (!lastFetched) return '—';
    const now = new Date();
    const diffMs = now.getTime() - lastFetched.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    return `${diffMins} min ago`;
  }, [lastFetched]);

  const columns: GridColDef[] = useMemo(() => {
    if (!reportData?.columns) return [];

    return reportData.columns.map((col) => ({
      field: col.key,
      headerName: col.label,
      flex: 1,
      minWidth: col.key === 'employee_id' ? 120 : 150,
      renderCell:
        col.key === 'employee_status_code'
          ? ({ value }) => (
              <Chip
                label={
                  value === 'A'
                    ? 'Active'
                    : value === 'T'
                    ? 'Terminated'
                    : value || '—'
                }
                color={value === 'A' ? 'success' : 'default'}
                size="small"
                variant="outlined"
              />
            )
          : undefined,
    }));
  }, [reportData?.columns]);

  const rows = useMemo(() => {
    return filteredRows.map((row, index) => ({
      id: row.employee_id || index,
      ...row,
    }));
  }, [filteredRows]);

  // Count active filters for badge (total selected values across all filters)
  const activeFilterCount = useMemo(() => {
    const fieldFilterCount = Object.values(filters).reduce(
      (count, values) => count + (values?.length || 0),
      0
    );
    const completionFilterCount = completionFilter !== 'all' ? 1 : 0;
    return fieldFilterCount + completionFilterCount;
  }, [filters, completionFilter]);

  // Get all active filters as array for displaying chips
  const activeFiltersArray = useMemo(() => {
    const result: Array<{
      fieldKey: string;
      fieldLabel: string;
      value: string;
    }> = [];
    for (const [fieldKey, values] of Object.entries(filters)) {
      if (values && values.length > 0) {
        const fieldDef = fields.find((f) => f.field_key === fieldKey);
        const fieldLabel = fieldDef?.field_label || fieldKey;
        for (const value of values) {
          result.push({ fieldKey, fieldLabel, value });
        }
      }
    }
    return result;
  }, [filters, fields]);

  const hasNoFields = fields.length === 0;
  const hasNoData = !reportData?.rows || reportData.rows.length === 0;

  return (
    <Box>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Custom Fields Data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and export custom field values for all employees
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                variant="outlined"
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCsv}
                variant="contained"
                color="primary"
                disabled={downloading || hasNoFields || hasNoData}
              >
                {downloading ? 'Downloading...' : 'Download full report CSV'}
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <PageSpinner />
          ) : hasNoFields ? (
            <Alert severity="info">
              No custom fields have been configured yet. Create custom fields in
              the "Field Management" tab to see employee data here.
            </Alert>
          ) : hasNoData ? (
            <Alert severity="info">
              No employee data available for the configured custom fields.
            </Alert>
          ) : (
            <Box>
              {/* Stats Cards */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 3,
                  flexWrap: 'wrap',
                }}
              >
                <StatCard
                  icon={<GroupIcon />}
                  label="Total Employees"
                  value={reportData?.rows?.length ?? 0}
                />
                <StatCard
                  icon={<TableChartIcon />}
                  label="Custom Fields"
                  value={fields.length}
                />
                <StatCard
                  icon={<TrendingUpIcon />}
                  label="Completion Rate"
                  value={completionRate !== null ? `${completionRate}%` : 'N/A'}
                />
                <StatCard
                  icon={<AccessTimeIcon />}
                  label="Last Updated"
                  value={lastFetchedDisplay}
                />
              </Box>

              {/* Unified Search & Filter Section */}
              <Paper
                variant="outlined"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: hasActiveFilters ? 'primary.200' : 'divider',
                  transition: 'border-color 0.2s ease',
                }}
              >
                {/* Search Row with Integrated Filter Toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    gap: 1,
                    backgroundColor: 'white',
                  }}
                >
                  <SearchIcon sx={{ color: 'text.disabled', ml: 0.5 }} />
                  <TextField
                    placeholder="Search by name, ID, or any field value..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    size="small"
                    variant="standard"
                    fullWidth
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            aria-label="clear search"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Completion Status Filter */}
                  {requiredFieldsInData.length > 0 && (
                    <ToggleButtonGroup
                      value={completionFilter}
                      exclusive
                      onChange={(_e, value) =>
                        value && setCompletionFilter(value)
                      }
                      size="small"
                      sx={{
                        backgroundColor: 'grey.100',
                        borderRadius: 1,
                        p: 0.375,
                        gap: 0.375,
                        border: 'none',
                        '& .MuiToggleButtonGroup-grouped': {
                          border: 'none',
                          borderRadius: '4px !important',
                          mx: 0,
                        },
                        '& .MuiToggleButton-root': {
                          textTransform: 'none',
                          px: 1.5,
                          py: 0.375,
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: 'text.secondary',
                          border: 'none',
                          '&:hover': {
                            backgroundColor: 'grey.200',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'white',
                            color: 'text.primary',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            '&:hover': {
                              backgroundColor: 'white',
                            },
                          },
                        },
                      }}
                    >
                      <ToggleButton value="all">All</ToggleButton>
                      <ToggleButton value="completed">Completed</ToggleButton>
                      <ToggleButton value="incomplete">Incomplete</ToggleButton>
                    </ToggleButtonGroup>
                  )}

                  {/* Filter Toggle */}
                  {Object.keys(filterOptions).length > 0 && (
                    <Button
                      variant="text"
                      color={activeFilterCount > 0 ? 'primary' : 'inherit'}
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                      endIcon={
                        showFilters ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )
                      }
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1,
                        fontWeight: activeFilterCount > 0 ? 600 : 400,
                        backgroundColor:
                          activeFilterCount > 0 ? 'primary.50' : 'transparent',
                        '&:hover': {
                          backgroundColor:
                            activeFilterCount > 0 ? 'primary.100' : 'grey.100',
                        },
                      }}
                    >
                      <TuneIcon sx={{ fontSize: 18, mr: 0.75 }} />
                      <span>Filters</span>
                      {activeFilterCount > 0 && (
                        <Box
                          component="span"
                          sx={{
                            ml: 0.75,
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 1,
                            backgroundColor: 'primary.main',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            lineHeight: 1.4,
                          }}
                        >
                          {activeFilterCount}
                        </Box>
                      )}
                    </Button>
                  )}
                </Box>

                {/* Active Filters Row - Inline display */}
                {(activeFiltersArray.length > 0 ||
                  completionFilter !== 'all') && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      px: 2,
                      py: 1.25,
                      backgroundColor: 'grey.50',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {completionFilter !== 'all' && (
                      <Chip
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                              }}
                            >
                              Status
                            </Box>
                            <Box component="span" sx={{ fontWeight: 500 }}>
                              {completionFilter === 'completed'
                                ? 'Completed'
                                : 'Incomplete'}
                            </Box>
                          </Box>
                        }
                        size="small"
                        onDelete={() => setCompletionFilter('all')}
                        sx={{
                          backgroundColor: 'white',
                          border: '1px solid',
                          borderColor: 'grey.300',
                          height: 26,
                          '& .MuiChip-label': {
                            px: 1,
                          },
                          '& .MuiChip-deleteIcon': {
                            fontSize: '1rem',
                            color: 'grey.500',
                            '&:hover': { color: 'error.main' },
                          },
                        }}
                      />
                    )}
                    {activeFiltersArray.map(
                      ({ fieldKey, fieldLabel, value }) => (
                        <Chip
                          key={`${fieldKey}-${value}`}
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {fieldLabel}
                              </Box>
                              <Box component="span" sx={{ fontWeight: 500 }}>
                                {value}
                              </Box>
                            </Box>
                          }
                          size="small"
                          onDelete={() => handleRemoveFilter(fieldKey, value)}
                          sx={{
                            backgroundColor: 'white',
                            border: '1px solid',
                            borderColor: 'grey.300',
                            height: 26,
                            '& .MuiChip-label': {
                              px: 1,
                            },
                            '& .MuiChip-deleteIcon': {
                              fontSize: '1rem',
                              color: 'grey.500',
                              '&:hover': { color: 'error.main' },
                            },
                          }}
                        />
                      )
                    )}
                    <Button
                      size="small"
                      onClick={handleClearFilters}
                      sx={{
                        ml: 'auto',
                        color: 'text.secondary',
                        fontSize: '0.8125rem',
                        '&:hover': {
                          color: 'error.main',
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      Clear all
                    </Button>
                  </Box>
                )}

                {/* Expandable Filter Options */}
                <Collapse in={showFilters}>
                  <Box
                    sx={{
                      px: 2,
                      py: 2,
                      backgroundColor: 'grey.50',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fill, minmax(250px, 1fr))',
                        gap: 1.5,
                      }}
                    >
                      {Object.entries(filterOptions).map(
                        ([fieldKey, options]) => {
                          const fieldDef = fields.find(
                            (f) => f.field_key === fieldKey
                          );
                          const label = fieldDef?.field_label || fieldKey;
                          const selectedValues = filters[fieldKey] || [];
                          return (
                            <Autocomplete
                              key={fieldKey}
                              multiple
                              size="small"
                              options={options}
                              value={selectedValues}
                              onChange={(_event, newValue) =>
                                handleFilterChange(fieldKey, newValue)
                              }
                              disableCloseOnSelect
                              limitTags={1}
                              getLimitTagsText={(more) => `+${more}`}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={label}
                                  placeholder={
                                    selectedValues.length === 0
                                      ? 'Any'
                                      : undefined
                                  }
                                  size="small"
                                />
                              )}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={option}
                                    label={option}
                                    size="small"
                                    sx={{
                                      maxWidth: 70,
                                      height: 20,
                                      '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        px: 0.75,
                                        fontSize: '0.7rem',
                                      },
                                      '& .MuiChip-deleteIcon': {
                                        fontSize: '0.8rem',
                                        mr: 0.25,
                                      },
                                    }}
                                  />
                                ))
                              }
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'white',
                                  minHeight: 36,
                                  ...(selectedValues.length > 0 && {
                                    borderColor: 'primary.main',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'primary.main',
                                      borderWidth: 1.5,
                                    },
                                  }),
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: '0.8125rem',
                                },
                              }}
                              ListboxProps={{
                                sx: { maxHeight: 200, fontSize: '0.875rem' },
                              }}
                            />
                          );
                        }
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </Paper>

              {/* Results Bar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {hasActiveFilters ? (
                    <>
                      <Box
                        component="span"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                      >
                        {rows.length}
                      </Box>
                      <span> of </span>
                      <span>
                        {reportData?.rows?.length} employees match your filters
                      </span>
                    </>
                  ) : (
                    <>
                      <Box
                        component="span"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                      >
                        {rows.length}
                      </Box>
                      <span> employees</span>
                    </>
                  )}
                </Typography>
              </Box>

              <DataTable
                rows={rows}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                disableRowSelectionOnClick
                autoHeight
              />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}

function StatCard({ icon, label, value }: StatCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flex: 1,
        minWidth: 180,
      }}
    >
      <Box
        sx={{
          p: 1,
          borderRadius: 1.5,
          backgroundColor: 'grey.100',
          color: 'grey.600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

export default CustomFieldsReportTab;
